import {
	ButtonPropsColorOverrides,
	ChipPropsColorOverrides
} from '@mui/material';
import { ZIndex } from '@mui/material/styles/zIndex';
import { OverridableStringUnion } from '@mui/types';
import { GridRowParams } from '@mui/x-data-grid';
import { InputHTMLAttributes } from 'react';
import { IntlShape } from 'react-intl';

declare global {
	// eslint-disable-next-line vars-on-top, no-var
	var intl: ITranslator;
}

export interface ICommon {
	id: string;
	version: number;
	modifiedTime?: number;
	createdTime?: number;
}

export interface IProject extends ICommon {
	projectId: string;
}

export interface IDataResponse<T extends Partial<ICommon>> {
	data?: T[];
	total: number;
}

export interface IItemResponse<T extends Partial<ICommon>> {
	item: T;
}

export interface IProps {
	children?: React.ReactElement;
}

export interface IChildrenProps {
	children: React.ReactElement;
}

export interface IWindowAwareProps extends IChildrenProps {
	window?: () => Window;
}

export enum TimeUnit {
	seconds = 'seconds',
	minutes = 'minutes',
	hours = 'hours',
	days = 'days',
	weeks = 'weeks',
	months = 'months',
	years = 'years'
}

export enum UserStatus {
	enabled = 'enabled',
	disabled = 'disabled',
	invited = 'invited',
	unknown = 'unknown'
}

export enum UsersActions {
	delete = 'delete',
	suspend = 'suspend',
	activate = 'activate',
	editTenants = 'editTenants',
	editSingle = 'editSingle'
}

export enum TenantStatus {
	active = 'active',
	disabled = 'disabled'
}

export type ChipPropsColor = OverridableStringUnion<
	| 'default'
	| 'primary'
	| 'secondary'
	| 'error'
	| 'info'
	| 'success'
	| 'warning',
	ChipPropsColorOverrides
>;

export const zIndex: ZIndex = {
	mobileStepper: 1000,
	speedDial: 1050,
	fab: 1050,
	appBar: 1100,
	drawer: 1200,
	modal: 1300,
	snackbar: 1400,
	tooltip: 1500
};

export enum PopperPlacements {
	auto = 'auto',
	bottomStart = 'bottom-start',
	bottomEnd = 'bottom-end',
	topStart = 'top-start',
	topEnd = 'top-end'
}

export type ColorPropsUnion =
	| 'inherit'
	| 'primary'
	| 'secondary'
	| 'success'
	| 'error'
	| 'info'
	| 'warning';

export type ButtonColorProps = OverridableStringUnion<
	ColorPropsUnion,
	ButtonPropsColorOverrides
>;

export enum TokenResponseMethod {
	TokenInBody = 'onBody',
	SessionCookies = 'cookie'
}

export type Translate = (
	id: string,
	values?: any,
	defaultValue?: string
) => string;
export type FormatDateTime = (value?: any) => string;

export interface ITranslator extends IntlShape {
	tr: Translate;
	formatDateTime: FormatDateTime;
}
export interface IDataGridProps {
	actions?: React.ReactNode;
	leftActions?: React.ReactNode;
}

export interface IDialogProps<T, K = void> {
	onSubmit: (values?: T) => Promise<K>;
	onCancel: () => void;
}

export interface IActionDialogProps {
	onCreate: () => void;
	onCancel: () => void;
}

export interface IRowAction {
	key: string;
	label: string;
	Dialog: React.FC<IRowActionDialogProps>;
}

export interface IRowActionDialogProps {
	onComplete: () => void;
	row?: GridRowParams;
}

export interface TestableHTMLElement
	extends InputHTMLAttributes<HTMLInputElement> {
	'data-testid'?: string;
}

export interface IRoute {
	navigationAccess: boolean;
	labelId?: string;
	disableDevRev?: boolean;
	fullWidth?: boolean;
	mobileSupported?: boolean;
}

export interface IRoutePath extends IRoute {
	path: string;
}

export enum FormFieldType {
	string = 'string',
	boolean = 'boolean',
	number = 'number',
	context = 'context'
}

export interface IFormFieldRow {
	type: `${FormFieldType}`;
	key: string;
	value: any;
}

export interface IOption {
	value: string;
	label: string;
}
